import React, { useState } from 'react';
import Table from './Structures/Table';
import { Link, useParams } from 'react-router-dom';

const CompanyLobbyists = ({ lobbyists }) => {
  const { state } = useParams();

  // Define the fields we care about.
  // Each field may include an optional formatter.
  const fieldsOfInterest = [
    {
      key: 'BeginDate',
      label: 'Begin Date',
      formatter: (val) => (val ? new Date(val).toLocaleDateString() : ''),
    },
    {
      key: 'StopDate',
      label: 'Stop Date',
      formatter: (val) => (val ? new Date(val).toLocaleDateString() : ''),
    },
    {
      key: 'LobbyistName',
      label: 'Lobbyist Name',
      // Compute the lobbyist name from multiple fields.
      formatter: (client) => {
        const title = client.LobbyistTitle || '';
        const first = client.LobbyistFirstName || '';
        const middle = client.LobbyistMiddleName ? client.LobbyistMiddleName + ' ' : '';
        const last = client.LobbyistLastName || '';
        return `${title} ${first} ${middle}${last}`.trim();
      },
    },
    {
      key: 'LobbyEngagementStatus',
      label: 'Status',
    },
    {
      key: 'CompensationRange',
      label: 'Compensation Range',
      // Compute a financial range based on SalaryLow and SalaryHigh.
      formatter: (client) => {
        const low = client.SalaryLow;
        const high = client.SalaryHigh;
        if (low == null && high == null) return '';
        return `$${low ? low.toLocaleString() : '0'} - $${high ? high.toLocaleString() : '0'}`;
      },
    },
  ];

  // Filter out any field that is always null/empty.
  const dynamicFields = fieldsOfInterest.filter((field) => {
    if (field.key === 'CompensationRange') {
      return lobbyists.some((client) => client.SalaryLow != null || client.SalaryHigh != null);
    } else if (field.key === 'LobbyistName') {
      // For LobbyistName, compute the name and check if it's non-empty.
      return lobbyists.some((client) => {
        const name = field.formatter(client);
        return name !== '';
      });
    } else {
      return lobbyists.some(
        (client) => client[field.key] !== null && client[field.key] !== undefined && client[field.key] !== ''
      );
    }
  });

  // Set default sort field. Prefer LobbyistName if available.
  const defaultSortField = dynamicFields.find((f) => f.key === 'LobbyistName')
    ? 'LobbyistName'
    : dynamicFields[0]?.key || '';
  const [sortingField, setSortingField] = useState(defaultSortField);
  const [sortingDirection, setSortingDirection] = useState('asc');

  // Sorting function.
  const sortLobbyists = (lobbyists) => {
    return [...lobbyists].sort((a, b) => {
      let valueA, valueB;
      if (sortingField === 'CompensationRange') {
        // Use SalaryLow as a proxy for sorting.
        valueA = a.SalaryLow || 0;
        valueB = b.SalaryLow || 0;
      } else if (sortingField === 'LobbyistName') {
        valueA = dynamicFields.find((f) => f.key === 'LobbyistName').formatter(a);
        valueB = dynamicFields.find((f) => f.key === 'LobbyistName').formatter(b);
      } else {
        valueA = a[sortingField] || '';
        valueB = b[sortingField] || '';
      }
      if (valueA < valueB) return sortingDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortingDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (field) => {
    if (sortingField === field) {
      setSortingDirection(sortingDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortingField(field);
      setSortingDirection('asc');
    }
  };

  // Build headers dynamically.
  const headers = dynamicFields.map((field) => (
    <span key={field.key} onClick={() => handleSort(field.key)} className="cursor-pointer">
      {field.label}
    </span>
  ));

  // Build rows dynamically.
  const rows = sortLobbyists(lobbyists).map((lobbyist) =>
    dynamicFields.map((field) => {
      let cellContent;
      if (field.key === 'CompensationRange' || field.key === 'LobbyistName') {
        cellContent = field.formatter(lobbyist);
      } else if (field.formatter) {
        cellContent = field.formatter(lobbyist[field.key]);
      } else {
        cellContent = lobbyist[field.key] || '';
      }
      // For LobbyistName, wrap it in a Link.
      if (field.key === 'LobbyistName') {
        cellContent = (
          <Link key={field.key} to={`/${state}/lobbyist/${lobbyist.LobbyistID}`} className="lobbyCompanyName">
            {cellContent}
          </Link>
        );
      }
      return <span key={field.key}>{cellContent}</span>;
    })
  );

  return (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] 2xl:p-8 xl:p-6 p-5">
      <div className="border-b border-[#E5E7EB] mb-5 pb-2.5">
        <h2 className="font-playfair text-[28px] text-darkblue font-medium leading-8">Lobbyist</h2>
      </div>
      {lobbyists && lobbyists.length > 0 ? (
        <Table headers={headers} rows={rows} />
      ) : (
        <p className="font-playfair text-[28px] text-darkblue font-medium leading-8">
          No lobbyists found for this company.
        </p>
      )}
    </div>
  );
};

export default CompanyLobbyists;
